import { AVERAGE_UNAVAILABLE } from '../averages';
import { EMPTY_RATE } from '../format-rate';
import type { KpiTileData } from '~analytics-components/kpi-tile';

/** A window the source could not read at all, as opposed to one read with an
 * empty denominator (which keeps its own null rate and its own label). */
export const UNREAD_WINDOW_LABEL = 'Date N/A';

/** The hover text on a tile that shows UNREAD_WINDOW_LABEL as "N/A". */
export const UNREAD_WINDOW_HINT = `${UNREAD_WINDOW_LABEL}: no figure for this window`;

export const COUNT_AVERAGE_DIGITS = { maximumFractionDigits: 1 };

const CURRENCY_DIGITS = {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
};

/** `toLocaleString` throws above this. */
const MAX_FRACTION_DIGITS = 20;

/** `COUNT_AVERAGE_DIGITS`'s single decimal reads a large count cleanly, and
 * `toLocaleString`'s own default stops at three, so either rounds a small
 * nonzero figure down to something indistinguishable from zero. Under 1, take
 * enough decimals to keep two significant digits: 0.015 and 0.00041 both live. */
const digitsFor = (
    value: number | typeof AVERAGE_UNAVAILABLE,
    isCurrency: boolean | undefined,
    fromOne: { maximumFractionDigits?: number }
) => {
    if (isCurrency) return CURRENCY_DIGITS;
    if (typeof value !== 'number' || value === 0 || Math.abs(value) >= 1) {
        return fromOne;
    }
    return {
        maximumFractionDigits: Math.min(
            MAX_FRACTION_DIGITS,
            Math.ceil(-Math.log10(Math.abs(value))) + 1
        ),
    };
};

export interface FigureUnit {
    isCurrency?: boolean;
    /** A 0..1 rate: KpiTile tweens it as a raw number, then formats it like
     * `formatRate` itself would. */
    isPercent?: boolean;
}

/**
 * One tile's value and its formatting, shared by `trio` and `rate-trio` so a
 * dollar figure reads the same whichever form carries it.
 *
 * `fromOne` is the digit rule for a figure at or above 1; below it the rule is
 * significant digits, whatever the caller asked for.
 */
export const figureTile = (
    value: number | null | typeof AVERAGE_UNAVAILABLE,
    unit: FigureUnit = {},
    fromOne: { maximumFractionDigits?: number } = {}
): Omit<KpiTileData, 'title'> => {
    // Too long for a tile in full, so it reads "N/A" and names itself on
    // hover; screen readers still get the full label.
    if (value === AVERAGE_UNAVAILABLE) {
        return {
            value: UNREAD_WINDOW_LABEL,
            shortValue: 'N/A',
            shortValueTitle: UNREAD_WINDOW_HINT,
        };
    }
    // A read window with no denominator: the figure is undefined, not zero.
    if (value === null) return { value: EMPTY_RATE };
    if (unit.isPercent) return { value, isPercent: true };
    return {
        value,
        isCurrency: unit.isCurrency,
        ...digitsFor(value, unit.isCurrency, fromOne),
    };
};
